import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Profile } from "@/hooks/designer-grid/types";

export const usePortfolioFeed = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 12;

  const result = useQuery({
    queryKey: [
      "portfolio-feed",
      {
        limit,
        page,
      },
    ],
    queryFn: async () => {
      console.log('Portfolio feed: Starting query with params', { limit, page });
      // Construct query parameters
      const params = {
        limit_count: limit,
        offset_value: (page - 1) * limit,
      };

      // Call the new RPC function specifically for portfolio feed
      console.log('Portfolio feed: Calling RPC with params', params);
      const { data, error } = await supabase.rpc(
        "get_featured_portfolio_feed",
        params
      );

      console.log('Portfolio feed: RPC response', { data, error });
      if (error) throw error;

      // Check if data exists and has the expected structure
      if (!data || !data.profiles || !Array.isArray(data.profiles)) {
        console.warn('Portfolio feed: No data or invalid data structure', { data });
        return { profiles: [], totalCount: 0, pagination: null };
      }

      console.log('Portfolio feed: Raw data received', { 
        profilesCount: data.profiles.length,
        firstProfile: data.profiles[0],
        totalCount: data.total_count,
        allProfiles: data.profiles,
        firstProfileUpdatedAt: data.profiles[0]?.updated_at,
        firstProfileUpdatedAtType: typeof data.profiles[0]?.updated_at,
        firstProfileUpdatedAtFormatted: (data.profiles[0] as any)?.updated_at_formatted,
        firstProfileUpdatedAtEpoch: (data.profiles[0] as any)?.updated_at_epoch
      });

      // Process profiles to ensure they match the Profile type
      const profiles = data.profiles
        .filter((profile: any) => profile && profile.id) // Filter out undefined/null profiles
        .map((profile: any) => ({
          id: profile.id,
          username: profile.username,
          username_slug: profile.username_slug,
          profession: profile.profession,
          location: profile.location,
          avatar_url: profile.avatar_url,
          is_admin: profile.is_admin || false,
          is_featured: profile.is_featured || false,
          skills: profile.skills || [],
          view_count: profile.view_count || 0,
          like_count: profile.like_count || 0,
          is_liked: profile.is_liked || false,
          display_name: profile.display_name || profile.username,
          hire_me_enabled: profile.hire_me_enabled || false,
          trending_score: profile.trending_score || 0,
          is_trending: (profile.trending_score || 0) > 10,
          portfolio_items: profile.portfolio_items || [],
          updated_at: profile.updated_at
        }));

      const totalCount =
        typeof data.total_count === "number" ? data.total_count : 0;
      const totalPages = Math.ceil(totalCount / limit);
      const currentPage = page;
      const hasNextPage = currentPage < totalPages;
      const hasPrevPage = currentPage > 1;

      // Return both the profiles array and pagination info
      return {
        profiles,
        totalCount,
        pagination: {
          currentPage,
          totalPages,
          hasNextPage,
          hasPrevPage,
          totalCount,
        },
      };
    },
  });

  // Function to handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return {
    ...result,
    handlePageChange,
  };
}; 